"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useDebounceCallback } from "./useDebounceCallback";
import { useEventListener } from "./useEventListener";
import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect";
type WindowSize<T extends number | undefined = number | undefined> = {
  width: T;
  height: T;
};
type UseWindowSizeOptions<InitializeWithValue extends boolean | undefined> = {
  initializeWithValue: InitializeWithValue;
  delay?: number;
};
const IS_SERVER = typeof window === "undefined";

// SSR version of useWindowSize.
export function useWindowSize(options: UseWindowSizeOptions<false>): WindowSize;

// CSR version of useWindowSize.
export function useWindowSize(options?: Partial<UseWindowSizeOptions<true>>): WindowSize<number>;
export function useWindowSize(t0) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    initializeWithValue: t2
  } = options;
  let initializeWithValue;
  if ($[2] !== t2) {
    initializeWithValue = t2 === undefined ? true : t2;
    if (IS_SERVER) {
      initializeWithValue = false;
    }
    $[2] = t2;
    $[3] = initializeWithValue;
  } else {
    initializeWithValue = $[3];
  }
  let t3;
  if ($[4] !== initializeWithValue) {
    t3 = () => {
      if (initializeWithValue) {
        return {
          width: window.innerWidth,
          height: window.innerHeight
        };
      }
      return {
        width: undefined,
        height: undefined
      };
    };
    $[4] = initializeWithValue;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [windowSize, setWindowSize] = useState(t3);
  const debouncedSetWindowSize = useDebounceCallback(setWindowSize, options.delay);
  let t4;
  if ($[6] !== debouncedSetWindowSize || $[7] !== options.delay) {
    t4 = function handleSize() {
      const setSize = options.delay ? debouncedSetWindowSize : setWindowSize;
      setSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    $[6] = debouncedSetWindowSize;
    $[7] = options.delay;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleSize = t4;
  useEventListener("resize", handleSize);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useIsomorphicLayoutEffect(handleSize, t5);
  return windowSize;
}